import React from "react";
import "styles/legacy/UploadNewTitle.css";

interface NewTitleMetadataProps {
  metadata: {
    title: string;
    language: string;
    category: string;
    genre: string;
    description: string;
    format: string;
    ageRating: string;
    comfortLevel: string;
    copyrightOwner: string;
    publisher: string;
    releaseDate: string;
    festivalParticipations: string;
    awards: string;
  };
  onMetadataChange: (
    field: keyof NewTitleMetadataProps["metadata"],
    value: string
  ) => void;
  onBlur?: (field: string) => void;
  touchedFields?: Record<string, boolean>;
}

const isEmpty = (val: string) => val.trim() === "";

const FormField = ({
  label,
  htmlFor,
  children,
  invalid,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
  invalid?: boolean;
}) => (
  <div className="form-field">
    <label className="form-label" htmlFor={htmlFor}>
      {label}
    </label>
    <div className="field-control">{children}</div>
  </div>
);

const SelectField = ({
  label,
  placeholder,
  value = "",
  onChange = () => {},
  onBlur,
  invalid,
  children,
}: {
  label: string;
  placeholder: string;
  value?: string;
  onChange?: (val: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
  children: React.ReactNode;
}) => {
  const isPlaceholder = value === "";

  return (
    <FormField label={label} invalid={invalid}>
      <select
        className={`select ${isPlaceholder ? "placeholder" : ""} ${
          invalid ? "error-border" : ""
        }`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {children}
      </select>
    </FormField>
  );
};

const NewTitleMetadata: React.FC<NewTitleMetadataProps> = ({
  metadata,
  onMetadataChange,
  onBlur,
  touchedFields = {},
}) => {
  const genreOptionsByCategory: Record<
    string,
    { value: string; label: string }[]
  > = {
    cinema: [
      { value: "action", label: "Action" },
      { value: "adventure", label: "Adventure" },
      { value: "animation", label: "Animation" },
      { value: "biographical", label: "Biographical" },
      { value: "cyberpunk", label: "Cyberpunk" },
      { value: "documentary", label: "Documentary" },
      { value: "experimental", label: "Experimental" },
      { value: "fantasy", label: "Fantasy" },
      { value: "historical", label: "Historical" },
      { value: "horror", label: "Horror" },
      { value: "mystery", label: "Mystery" },
      { value: "mythology", label: "Mythology" },
      { value: "narrative", label: "Narrative" },
      { value: "noir", label: "Noir" },
      { value: "post-apocalyptic", label: "Post-Apocalyptic" },
      { value: "psychological", label: "Psychological" },
      { value: "romance", label: "Romance" },
      { value: "sci-fi", label: "Sci-Fi" },
      { value: "silent", label: "Silent" },
      { value: "supernatural", label: "Supernatural" },
      { value: "surreal", label: "Surreal" },
      { value: "thriller", label: "Thriller" },
      { value: "war", label: "War" },
      { value: "western", label: "Western" },
    ],
    theatre: [
      { value: "absurdist", label: "Absurdist" },
      { value: "cabaret", label: "Cabaret" },
      { value: "comedy", label: "Comedy" },
      { value: "drama", label: "Drama" },
      { value: "epic", label: "Epic" },
      { value: "experimental", label: "Experimental" },
      { value: "farce", label: "Farce" },
      { value: "improvisation", label: "Improvisation" },
      { value: "masked", label: "Masked" },
      { value: "melodrama", label: "Melodrama" },
      { value: "mime", label: "Mime" },
      { value: "monologue", label: "Monologue" },
      { value: "musical", label: "Musical" },
      { value: "one-act", label: "One-Act" },
      { value: "opera", label: "Opera" },
      { value: "performance-art", label: "Performance Art" },
      { value: "physical", label: "Physical" },
      { value: "political", label: "Political" },
      { value: "puppetry", label: "Puppetry" },
      { value: "ritual", label: "Ritual" },
      { value: "satire", label: "Satire" },
      { value: "shakespearean", label: "Shakespearean" },
      { value: "shadow-play", label: "Shadow Play" },
      { value: "tragedy", label: "Tragedy" },
      { value: "vaudeville", label: "Vaudeville" },
      { value: "verbatim", label: "Verbatim" },
    ],
    music: [
      { value: "acoustic", label: "Acoustic" },
      { value: "ambient", label: "Ambient" },
      { value: "baroque", label: "Baroque" },
      { value: "blues", label: "Blues" },
      { value: "choral", label: "Choral" },
      { value: "classical", label: "Classical" },
      { value: "concert", label: "Concert" },
      { value: "country", label: "Country" },
      { value: "electronic", label: "Electronic" },
      { value: "experimental", label: "Experimental" },
      { value: "folk", label: "Folk" },
      { value: "funk", label: "Funk" },
      { value: "gospel", label: "Gospel" },
      { value: "hip-hop", label: "Hip-Hop" },
      { value: "house", label: "House" },
      { value: "jazz", label: "Jazz" },
      { value: "metal", label: "Metal" },
      { value: "opera", label: "Opera" },
      { value: "orchestral", label: "Orchestral" },
      { value: "pop", label: "Pop" },
      { value: "psychedelic", label: "Psychedelic" },
      { value: "r&b", label: "R&B" },
      { value: "reggae", label: "Reggae" },
      { value: "rock", label: "Rock" },
      { value: "soul", label: "Soul" },
      { value: "soundscape", label: "Soundscape" },
      { value: "techno", label: "Techno" },
      { value: "trance", label: "Trance" },
      { value: "world", label: "World" },
    ],
  };

  const isInvalid = (field: string, value: string) =>
    touchedFields[field] && isEmpty(value);

  const isFutureDate = (dateStr: string) => {
    const inputDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return inputDate > today;
  };

  function formatDateForDisplay(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  }

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div className="form-inputs-column">
      <div className="metadata-section">
        <h2 className="section-label">General</h2>
        <div className="section-frame">
          <div className="field-row">
            <div className="field-wrapper">
              <FormField
                label="Title*"
                invalid={isInvalid("title", metadata.title)}
              >
                <input
                  className={`input ${
                    isInvalid("title", metadata.title) ? "error-border" : ""
                  }`}
                  value={metadata.title}
                  onChange={(e) => onMetadataChange("title", e.target.value)}
                  onBlur={() => onBlur?.("title")}
                  placeholder="Enter title"
                  maxLength={80}
                />
              </FormField>
            </div>
            <div className="field-wrapper">
              <SelectField
                label="Language*"
                placeholder="Select an option"
                value={metadata.language}
                onChange={(val) => onMetadataChange("language", val)}
                onBlur={() => onBlur?.("language")}
                invalid={isInvalid("language", metadata.language)}
              >
                <option value="arabic">Arabic</option>
                <option value="bengali">Bengali</option>
                <option value="chinese">Chinese</option>
                <option value="czech">Czech</option>
                <option value="danish">Danish</option>
                <option value="dutch">Dutch</option>
                <option value="english">English</option>
                <option value="finnish">Finnish</option>
                <option value="filipino">Filipino</option>
                <option value="french">French</option>
                <option value="german">German</option>
                <option value="greek">Greek</option>
                <option value="gujarati">Gujarati</option>
                <option value="hebrew">Hebrew</option>
                <option value="hindi">Hindi</option>
                <option value="hungarian">Hungarian</option>
                <option value="indonesian">Indonesian</option>
                <option value="italian">Italian</option>
                <option value="japanese">Japanese</option>
                <option value="kannada">Kannada</option>
                <option value="korean">Korean</option>
                <option value="malay">Malay</option>
                <option value="marathi">Marathi</option>
                <option value="norwegian">Norwegian</option>
                <option value="persian">Persian</option>
                <option value="polish">Polish</option>
                <option value="portuguese">Portuguese</option>
                <option value="romanian">Romanian</option>
                <option value="russian">Russian</option>
                <option value="spanish">Spanish</option>
                <option value="swahili">Swahili</option>
                <option value="swedish">Swedish</option>
                <option value="tamil">Tamil</option>
                <option value="telugu">Telugu</option>
                <option value="thai">Thai</option>
                <option value="turkish">Turkish</option>
                <option value="ukrainian">Ukrainian</option>
                <option value="vietnamese">Vietnamese</option>
              </SelectField>
            </div>
          </div>
          <div className="field-row">
            <div className="field-wrapper">
              <SelectField
                label="Category*"
                placeholder="Select a category"
                value={metadata.category}
                onChange={(val) => {
                  onMetadataChange("category", val);
                  onMetadataChange("genre", "");
                }}
                onBlur={() => onBlur?.("category")}
                invalid={isInvalid("category", metadata.category)}
              >
                <option value="cinema">Cinema</option>
                <option value="theatre">Theatre</option>
                <option value="music">Music</option>
              </SelectField>
            </div>
            <div className="field-wrapper">
              <SelectField
                label="Genre*"
                placeholder="Select a genre"
                value={metadata.genre}
                onChange={(val) => onMetadataChange("genre", val)}
                onBlur={() => onBlur?.("genre")}
                invalid={isInvalid("genre", metadata.genre)}
              >
                {genreOptionsByCategory[metadata.category]?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>
          <div className="text-row">
            <div className="textarea-wrapper">
              <FormField
                label="Description*"
                invalid={isInvalid("description", metadata.description)}
              >
                <textarea
                  className={`field-textarea ${
                    isInvalid("description", metadata.description)
                      ? "error-border"
                      : ""
                  }`}
                  rows={5}
                  value={metadata.description}
                  onChange={(e) =>
                    onMetadataChange("description", e.target.value)
                  }
                  onBlur={() => onBlur?.("description")}
                  placeholder="Enter a thorough description of the uploaded piece"
                  maxLength={2000}
                />
              </FormField>
            </div>
          </div>
        </div>
      </div>
      <div className="metadata-section">
        <h2 className="section-label">Technical</h2>
        <div className="section-frame">
          <div className="field-row">
            <div className="field-wrapper">
              <SelectField
                label="Format*"
                placeholder="Select a format"
                value={metadata.format}
                onChange={(val) => onMetadataChange("format", val)}
                onBlur={() => onBlur?.("format")}
                invalid={isInvalid("format", metadata.format)}
              >
                <option value="2d">2D</option>
                <option value="180">180</option>
                <option value="180-stereo">180 Stereo</option>
                <option value="360">360</option>
                <option value="360-stereo">360 Stereo</option>
              </SelectField>
            </div>
            <div className="field-wrapper">
              <SelectField
                label="Age suitability rating*"
                placeholder="Select an option"
                value={metadata.ageRating}
                onChange={(val) => onMetadataChange("ageRating", val)}
                onBlur={() => onBlur?.("ageRating")}
                invalid={isInvalid("ageRating", metadata.ageRating)}
              >
                <option value="G">G - General Audiences</option>
                <option value="PG">PG - Parental Guidance Suggested</option>
                <option value="PG-13">
                  PG-13 - Parents Strongly Cautioned
                </option>
                <option value="R">R - Restricted (17+)</option>
                <option value="NC-17">NC-17 - Adults Only (18+)</option>
              </SelectField>
            </div>
          </div>
          <div className="field-row">
            <div className="field-wrapper">
              <SelectField
                label="Comfort level"
                placeholder="Select an option"
                value={metadata.comfortLevel}
                onChange={(val) => onMetadataChange("comfortLevel", val)}
                onBlur={() => onBlur?.("comfortLevel")}
              >
                <option value="comfortable">
                  Comfortable - Minimal motion, static or slow movement,
                  suitable for all users
                </option>
                <option value="moderate">
                  Moderate - Some movement, smooth locomotion, may cause mild
                  discomfort
                </option>
                <option value="intense">
                  Intense - Fast motion, artificial movement, potential motion
                  sickness risk
                </option>
              </SelectField>
            </div>
          </div>
        </div>
      </div>
      <div className="metadata-section">
        <h2 className="section-label">Legal</h2>
        <div className="section-frame">
          <div className="field-row">
            <div className="field-wrapper">
              <FormField
                label="Copyright ownership*"
                invalid={isInvalid("copyrightOwner", metadata.copyrightOwner)}
              >
                <input
                  className={`input ${
                    isInvalid("copyrightOwner", metadata.copyrightOwner)
                      ? "error-border"
                      : ""
                  }`}
                  value={metadata.copyrightOwner}
                  onChange={(e) =>
                    onMetadataChange("copyrightOwner", e.target.value)
                  }
                  onBlur={() => onBlur?.("copyrightOwner")}
                  placeholder="Enter a name"
                />
                <p className="field-description">
                  Name of person or entity holding the rights
                </p>
              </FormField>
            </div>
            <div className="field-wrapper">
              <FormField label="Publisher">
                <input
                  value={metadata.publisher}
                  onChange={(e) =>
                    onMetadataChange("publisher", e.target.value)
                  }
                  onBlur={() => onBlur?.("publisher")}
                  placeholder="Enter a name"
                />
                <p className="field-description">
                  Fill if publisher is different from the creator
                </p>
              </FormField>
            </div>
          </div>
        </div>
      </div>
      <div className="metadata-section">
        <h2 className="section-label">Additional</h2>
        <div className="section-frame">
          <div className="field-row">
            <div className="field-wrapper">
              <FormField
                label="Release date"
                invalid={
                  !!metadata.releaseDate && isFutureDate(metadata.releaseDate)
                }
              >
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    value={
                      metadata.releaseDate
                        ? metadata.releaseDate.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      onMetadataChange("releaseDate", e.target.value)
                    }
                    max={todayStr}
                    className="date-input"
                  />
                  <span
                    className={`date-placeholder ${
                      metadata.releaseDate ? "hidden" : ""
                    }`}
                  >
                    Select a date
                  </span>
                  <span className="calendar-icon">📅</span>
                  {metadata.releaseDate && (
                    <span className="date-value">
                      {formatDateForDisplay(metadata.releaseDate)}
                    </span>
                  )}
                </div>
              </FormField>
            </div>
          </div>
          <div className="text-row">
            <div className="textarea-wrapper">
              <FormField label="Festival participations">
                <textarea
                  className="field-textarea"
                  rows={3}
                  value={metadata.festivalParticipations}
                  onChange={(e) =>
                    onMetadataChange("festivalParticipations", e.target.value)
                  }
                  onBlur={() => onBlur?.("festivalParticipations")}
                  placeholder="Each entry should be comma-separated"
                />
              </FormField>
            </div>
          </div>
          <div className="text-row">
            <div className="textarea-wrapper">
              <FormField label="Awards and other acknowledgements">
                <textarea
                  className="field-textarea"
                  rows={3}
                  value={metadata.awards}
                  onChange={(e) => onMetadataChange("awards", e.target.value)}
                  onBlur={() => onBlur?.("awards")}
                  placeholder="Each entry should be comma-separated"
                />
              </FormField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTitleMetadata;
